// AviationHomeLayout — cockpit/boarding-pass theme (theme-2-aviation)

import { DailyMessage } from "../components/child/DailyMessage";

const BOARDING_PASSES = [
  { key: "spelling",  code: "SPL", gate: "GATE A1", label: "SPELLING",  desc: "Spelling quiz",        color: "#10b981", light: "rgba(16,185,129,0.15)"  },
  { key: "meaning",   code: "DEF", gate: "GATE B2", label: "MEANINGS",  desc: "Word definitions",     color: "#f59e0b", light: "rgba(245,158,11,0.15)"   },
  { key: "synonym",   code: "SYN", gate: "GATE C3", label: "SYNONYMS",  desc: "Words with similar meanings", color: "#22d3ee", light: "rgba(34,211,238,0.15)" },
  { key: "antonym",   code: "ANT", gate: "GATE D4", label: "ANTONYMS",  desc: "Words with opposite meanings", color: "#a78bfa", light: "rgba(167,139,250,0.15)" },
];

const TYPE_LABEL_MAP = {
  spelling: "Spelling",
  meaning:  "Meanings",
  synonym:  "Synonyms",
  antonym:  "Antonyms",
};

export function AviationHomeLayout({
  user, data,
  onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
  settings,
}) {
  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const today = new Date().toDateString();
  const doneMap = {
    spelling: mySessions.some(s => new Date(s.date).toDateString() === today && s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym"),
    meaning:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "meaning"),
    synonym:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "synonym"),
    antonym:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "antonym"),
  };
  const onClickMap = {
    spelling: onStartTest,
    meaning:  onStartMeaningTest,
    synonym:  onStartSynonymTest,
    antonym:  onStartAntonymTest,
  };

  const streak   = data.streaks?.[user.id] || 0;
  const myPoints = data.points?.[user.id] || 0;
  const myGoalId = data.goals?.[user.id] || null;
  const myGoal   = myGoalId ? (data.rewards || []).find(r => r.id === myGoalId) : null;
  const goalPct  = myGoal ? Math.min(100, Math.round((myPoints / myGoal.cost) * 100)) : 0;
  const level    = Math.floor(myPoints / 100);

  // Circumference for the SVG circle gauge (r=45): 2πr ≈ 283
  const CIRC = 283;
  const goalOffset = CIRC - (CIRC * goalPct) / 100;

  // Recent sessions for flight log
  const recentSessions = mySessions
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  const appName = settings?.appName || "SpellWise";

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #0a1628 0%, #1a2744 50%, #0f1d32 100%)",
      fontFamily: "'Outfit', sans-serif",
      color: "white",
    }}>
      <style>{`
        .avia-status-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 359px) {
          .avia-status-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
        }
      `}</style>

      {/* Cockpit Header */}
      <header style={{
        background: "rgba(15,23,42,0.8)", backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(34,211,238,0.3)",
        padding: "12px 16px",
      }}>
        <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Radar icon */}
            <div style={{ position: "relative", width: 40, height: 40 }}>
              <div style={{ position: "absolute", inset: 0, border: "2px solid #22d3ee", borderRadius: "50%" }} />
              <div style={{ position: "absolute", inset: 8, background: "rgba(34,211,238,0.15)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg style={{ width: 16, height: 16, color: "#22d3ee" }} fill="#22d3ee" viewBox="0 0 24 24">
                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                </svg>
              </div>
              {/* Sweeping radar line */}
              <div style={{
                position: "absolute", inset: 0,
                border: "2px solid transparent",
                borderTopColor: "#22d3ee",
                borderRadius: "50%",
                animation: "spin 4s linear infinite",
              }} />
            </div>
            <div>
              <h1 style={{ fontFamily: "'Orbitron', sans-serif", color: "#22d3ee", fontSize: 13, letterSpacing: "0.15em" }}>{appName.toUpperCase()}</h1>
              <p style={{ color: "#0891b2", fontSize: 11 }}>FLIGHT ACADEMY</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontFamily: "'Orbitron', sans-serif", color: "#f59e0b", fontSize: 11, textShadow: "0 0 10px #f59e0b" }}>CAPT. {user.name.toUpperCase()}</p>
              <p style={{ color: "#0891b2", fontSize: 11 }}>Flight Hours: {myPoints}</p>
            </div>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "2px solid #f59e0b", background: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#f59e0b", fontSize: 14 }}>{user.avatar || user.name[0]}</span>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px 24px" }}>

        {/* Daily message — cockpit announcement style */}
        <DailyMessage userName={user.name} streak={streak} bgGradient="linear-gradient(135deg, #0c4a6e 0%, #164e63 100%)" />

        {/* Flight Status Panel */}
        <div style={{
          background: "rgba(30,41,59,0.5)", borderRadius: 12, padding: 16, marginBottom: 24,
          border: "1px solid rgba(34,211,238,0.2)",
          boxShadow: "0 0 20px rgba(0,200,255,0.15), inset 0 0 20px rgba(0,100,150,0.1)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#22d3ee", fontSize: 11, letterSpacing: "0.15em" }}>FLIGHT STATUS</span>
            <span style={{ color: "#4ade80", fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, background: "#4ade80", borderRadius: "50%", display: "inline-block", animation: "pulse 1s ease-in-out infinite" }} />
              ALL SYSTEMS GO
            </span>
          </div>
          <div className="avia-status-grid">
            {/* Goal gauge */}
            <div style={{ textAlign: "center" }}>
              <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto 8px" }}>
                <svg style={{ width: "100%", height: "100%" }} viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#1e3a5f" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#22d3ee" strokeWidth="8"
                    strokeDasharray={CIRC} strokeDashoffset={goalOffset}
                    transform="rotate(-90 50 50)" style={{ transition: "stroke-dashoffset .5s" }}/>
                </svg>
                <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", color: "#22d3ee", fontSize: 14, textShadow: "0 0 10px #22d3ee" }}>{goalPct}%</span>
              </div>
              <p style={{ color: "#64748b", fontSize: 11 }}>{myGoal ? "Goal" : "No Goal"}</p>
            </div>
            {/* Streak */}
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 64, height: 64, margin: "0 auto 8px", background: "rgba(15,23,42,0.5)", borderRadius: 8, border: "1px solid rgba(245,158,11,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#f59e0b", fontSize: 22, textShadow: "0 0 10px #f59e0b" }}>{streak}</span>
              </div>
              <p style={{ color: "#64748b", fontSize: 11 }}>Day Streak</p>
            </div>
            {/* Level */}
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 64, height: 64, margin: "0 auto 8px", background: "rgba(15,23,42,0.5)", borderRadius: 8, border: "1px solid rgba(74,222,128,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#4ade80", fontSize: 14, textShadow: "0 0 10px #4ade80", lineHeight: 1.2, textAlign: "center" }}>LVL{"\n"}{level}</span>
              </div>
              <p style={{ color: "#64748b", fontSize: 11 }}>Pilot Rank</p>
            </div>
          </div>
        </div>

        {/* Boarding Passes */}
        <p style={{ fontFamily: "'Orbitron', sans-serif", color: "#22d3ee", fontSize: 11, letterSpacing: "0.15em", marginBottom: 12, paddingLeft: 4 }}>SELECT DESTINATION</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {BOARDING_PASSES.map(({ key, code, gate, label, desc, color, light }) => {
            const done = doneMap[key];
            const onClick = onClickMap[key];
            return (
              <div
                key={key}
                onClick={onClick}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
                  borderLeft: `4px dashed ${done ? "#22c55e" : "#cccccc"}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex", alignItems: "center", gap: 16,
                  cursor: "pointer",
                  opacity: done ? 0.75 : 1,
                  transition: "transform .15s",
                }}
              >
                {/* Gate stub */}
                <div style={{ flexShrink: 0, textAlign: "center", borderRight: "1px dashed #cccccc", paddingRight: 14 }}>
                  <p style={{ fontFamily: "'Orbitron', sans-serif", color: "#1e293b", fontSize: 16, fontWeight: 700 }}>{code}</p>
                  <p style={{ color: "#64748b", fontSize: 10 }}>{gate}</p>
                </div>

                {/* Details */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#1e293b", fontSize: 12 }}>{label}</span>
                    {done ? (
                      <span style={{ background: "#dcfce7", color: "#166534", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999 }}>COMPLETED ✓</span>
                    ) : (
                      <span style={{ background: light, color: color, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, border: `1px solid ${color}` }}>BOARDING</span>
                    )}
                  </div>
                  <p style={{ color: "#64748b", fontSize: 11, marginBottom: done ? 0 : 6 }}>{desc}</p>
                  {!done && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 3, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: color, borderRadius: 999, width: "60%", transition: "width .4s" }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <svg style={{ width: 20, height: 20, color: done ? "#22c55e" : "#94a3b8", flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {done
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  }
                </svg>
              </div>
            );
          })}
        </div>

        {/* Flight Log */}
        <div style={{
          background: "rgba(30,41,59,0.5)", borderRadius: 12, padding: 16,
          border: "1px solid rgba(34,211,238,0.2)",
        }}>
          <h3 style={{ fontFamily: "'Orbitron', sans-serif", color: "#22d3ee", fontSize: 11, letterSpacing: "0.15em", marginBottom: 16 }}>FLIGHT LOG</h3>
          {recentSessions.length === 0 ? (
            <p style={{ color: "#475569", fontSize: 13, textAlign: "center", padding: "8px 0" }}>No flights logged yet — select a destination above!</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {recentSessions.map((s, i) => {
                const typeKey = s.type || "spelling";
                const typeLabel = TYPE_LABEL_MAP[typeKey] || "Spelling";
                const timeStr = new Date(s.date).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
                const mins = Math.floor((s.timeSec || 0) / 60);
                const secs = (s.timeSec || 0) % 60;
                const iconColor = typeKey === "meaning" ? "#f59e0b" : typeKey === "synonym" ? "#22d3ee" : typeKey === "antonym" ? "#a78bfa" : "#4ade80";
                return (
                  <div key={s.id || i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, background: `rgba(${iconColor === "#4ade80" ? "74,222,128" : iconColor === "#f59e0b" ? "245,158,11" : iconColor === "#22d3ee" ? "34,211,238" : "167,139,250"},0.15)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg style={{ width: 20, height: 20, color: iconColor }} fill={iconColor} viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: "#e2e8f0", fontSize: 13 }}>{typeLabel} Flight Completed</p>
                      <p style={{ color: "#475569", fontSize: 11 }}>{s.score}% · {mins > 0 ? `${mins}m ` : ""}{secs}s · {timeStr}</p>
                    </div>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", color: "#f59e0b", fontSize: 12, textShadow: "0 0 8px #f59e0b", flexShrink: 0 }}>+{s.ptsEarned || 0} XP</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </main>

      {/* Inline keyframe for radar animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
